import React, { useState, useEffect, useRef } from 'react';
import { Play, Eye, TrendingUp } from 'lucide-react';

const useCounter = (end, duration = 1200) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return [count, ref];
};

const reels = [
    { rank: 1, views: '142K', viewsNum: 142000, gradient: 'from-purple-600 via-violet-500 to-indigo-500', url: 'https://www.instagram.com/reel/DVVE0jJAfIy/', shortcode: 'DVVE0jJAfIy' },
    { rank: 2, views: '129K', viewsNum: 129000, gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://www.instagram.com/reel/DVXprcTiSai/', shortcode: 'DVXprcTiSai' },
    { rank: 3, views: '116K', viewsNum: 116000, gradient: 'from-emerald-500 via-teal-500 to-cyan-500', url: 'https://www.instagram.com/reel/DV1NLxlgkXh/', shortcode: 'DV1NLxlgkXh' },
    { rank: 4, views: '98.4K', viewsNum: 98400, gradient: 'from-amber-500 via-orange-500 to-red-500', url: 'https://www.instagram.com/reel/DV8qvbfFg6C/', shortcode: 'DV8qvbfFg6C' },
    { rank: 5, views: '80.8K', viewsNum: 80800, gradient: 'from-rose-600 via-pink-500 to-fuchsia-500', url: 'https://www.instagram.com/reel/DVEM2yLlvGp/', shortcode: 'DVEM2yLlvGp' },
    { rank: 6, views: '75.9K', viewsNum: 75900, gradient: 'from-cyan-500 via-blue-500 to-indigo-500', url: null, shortcode: null },
    { rank: 7, views: '45.1K', viewsNum: 45100, gradient: 'from-fuchsia-500 via-purple-500 to-violet-500', url: null, shortcode: null },
    { rank: 8, views: '42.3K', viewsNum: 42300, gradient: 'from-teal-500 via-emerald-500 to-green-500', url: null, shortcode: null },
    { rank: 9, views: '38K', viewsNum: 38000, gradient: 'from-orange-500 via-amber-500 to-yellow-500', url: null, shortcode: null },
];

const ReelCard = ({ reel }) => {
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);
    const isLinked = !!reel.url;
    const Tag = isLinked ? 'a' : 'div';
    const linkProps = isLinked
        ? { href: reel.url, target: '_blank', rel: 'noopener noreferrer' }
        : {};

    const thumbnailUrl = reel.shortcode
        ? `https://www.instagram.com/p/${reel.shortcode}/media/?size=l`
        : null;

    return (
        <Tag
            {...linkProps}
            className={`group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 ${isLinked ? 'hover:scale-[1.03] cursor-pointer' : ''}`}
            style={{ aspectRatio: '9/16' }}
        >
            {/* Gradient background (always present as fallback) */}
            <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />

            {/* Instagram thumbnail */}
            {thumbnailUrl && !imgError && (
                <img
                    src={thumbnailUrl}
                    alt={`Reel #${reel.rank}`}
                    onLoad={() => setImgLoaded(true)}
                    onError={() => setImgError(true)}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                />
            )}

            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />

            {/* Hover overlay (linked only) */}
            {isLinked && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 backdrop-blur-0 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100 flex flex-col items-center gap-2">
                        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                            <Play size={24} className="text-white ml-1" fill="white" />
                        </div>
                        <span className="text-white/80 text-xs font-medium">Watch on Instagram</span>
                    </div>
                </div>
            )}

            {/* Bottom info */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <div className="flex items-center gap-1.5">
                    <Eye size={14} className="text-white/70" />
                    <p className="text-white/90 text-sm font-bold font-mono">{reel.views}</p>
                </div>
            </div>

            {/* Rank badge */}
            <div className="absolute top-3 left-3">
                <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">
                    #{reel.rank}
                </span>
            </div>
        </Tag>
    );
};

export const TopReels = () => {
    const [totalViews, totalRef] = useCounter(767500);

    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
                <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold text-center mb-3">Top Performing Reels</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">
                    Reels That Went Viral
                </h2>
                <div className="mb-10" />

                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4">
                    {reels.map((reel, i) => (
                        <ReelCard key={i} reel={reel} />
                    ))}
                </div>

                {/* Total views summary */}
                <div ref={totalRef} className="mt-10 text-center">
                    <div className="inline-flex items-center gap-3 bg-white/[0.04] backdrop-blur-lg border border-white/10 rounded-full px-6 py-3">
                        <TrendingUp size={18} className="text-purple-400" />
                        <span className="text-white/60 text-sm">Combined views from top 9 reels:</span>
                        <span className="text-white font-black text-lg">{totalViews.toLocaleString()}+</span>
                    </div>
                </div>
            </div>
        </section>
    );
};
